/**
 * Contrato do caixa da casa: lançamentos, favorecidos, plano de contas e ajuda de custo.
 *
 * Papel: aqui ficam os valores fixos de onde nascem os enums do Postgres
 * (`TIPO_LANCAMENTO`, `FORMA_PAGAMENTO_LANCAMENTO`, `TIPO_ITEM_AJUDA_CUSTO`), os
 * nomes de categoria que têm regra própria, e os schemas que o modal, os
 * cadastros e as Functions de `/api/financas` usam. As somas do caixa ficam em
 * `financas-calculo.ts`.
 *
 * Como usar: o **valor não é campo**. É `valorBruto − desconto + acrescimo`, e
 * quem o calcula é `valorDoLancamento`. O corpo de criar e de editar um
 * lançamento é `salvarLancamentoSchema`, que não traz provisão nem vínculo: a
 * API decide a provisão — o lançamento novo nasce provisionado, o editado guarda
 * a sua — e preserva pedido, grupo de ajuda de custo, recorrência e parcelamento,
 * que o modal não mostra. Um vínculo mandado no corpo é descartado pelo parse.
 *
 * O tipo decide o lado do caixa: `receita` e `credito_movimentacao` entram,
 * `despesa` e `debito_movimentacao` saem. A movimentação move o saldo como
 * qualquer outro lançamento; o que ela não faz é aparecer nas tabelas de Saídas
 * e de Entradas do Fechamento.
 *
 * O **mês de um lançamento é a data dele**. Não há campo de competência, e uma
 * coluna à parte seria um segundo lugar dizendo o que a data já diz.
 */
import { z } from 'zod';

import {
  dataOpcionalSchema,
  dataSchema,
  idSchema,
  textoObrigatorio,
  textoOpcional,
} from './comum';
import {
  apenasDigitos,
  arredondarCentavos,
  competenciaSchema,
  converterDinheiro,
  dinheiroComPadraoZeroSchema,
  dinheiroOpcionalSchema,
  dinheiroSchema,
  valorPorUnidadeOpcionalSchema,
} from './formatos';

/**
 * O que um lançamento é, e de que lado do caixa ele fica.
 *
 * `receita` e `credito_movimentacao` entram; `despesa` e `debito_movimentacao`
 * saem. A movimentação é o dinheiro que anda sem ser receita nem despesa — um
 * estorno, um empréstimo, um ajuste de caixa.
 */
export const TIPO_LANCAMENTO = [
  'despesa',
  'receita',
  'credito_movimentacao',
  'debito_movimentacao',
] as const;

/**
 * Como o lançamento foi pago ou recebido, **na ordem do `select` do modal**.
 *
 * O filtro "Pagamento" da aba Lançamentos lista as mesmas sete em outra ordem, e
 * essa ordem mora na tela; reordenar este array muda o modal e o perfil do
 * favorecido, e nada mais.
 */
export const FORMA_PAGAMENTO_LANCAMENTO = [
  'pix',
  'boleto',
  'cartao_de_credito',
  'transferencia',
  'dinheiro',
  'debito_automatico',
  'cartao_de_debito',
] as const;

/** O que compõe um grupo de ajuda de custo. */
export const TIPO_ITEM_AJUDA_CUSTO = ['custo', 'diarias', 'medica', 'inss'] as const;

/**
 * A situação de um lançamento, como o selo da tabela a escreve.
 *
 * Não é coluna: sai de `pago` e `provisao` por `statusDoLancamento`, em
 * `financas-calculo.ts`, e a API a manda pronta em cada lançamento.
 */
export const STATUS_LANCAMENTO = ['pago', 'provisionado', 'lancado'] as const;

export const tipoLancamentoSchema = z.enum(TIPO_LANCAMENTO, { message: 'Tipo desconhecido.' });
export const formaPagamentoLancamentoSchema = z.enum(FORMA_PAGAMENTO_LANCAMENTO, {
  message: 'Forma de pagamento desconhecida.',
});
export const tipoItemAjudaCustoSchema = z.enum(TIPO_ITEM_AJUDA_CUSTO, {
  message: 'Tipo de item desconhecido.',
});
export const statusLancamentoSchema = z.enum(STATUS_LANCAMENTO, {
  message: 'Situação desconhecida.',
});

export type TipoLancamento = z.infer<typeof tipoLancamentoSchema>;
export type FormaPagamentoLancamento = z.infer<typeof formaPagamentoLancamentoSchema>;
export type TipoItemAjudaCusto = z.infer<typeof tipoItemAjudaCustoSchema>;
export type StatusLancamento = z.infer<typeof statusLancamentoSchema>;

/** O texto de cada forma, como o modal, a tabela e a exportação o escrevem. */
export const ROTULOS_FORMA: Record<FormaPagamentoLancamento, string> = {
  pix: 'Pix',
  boleto: 'Boleto',
  cartao_de_credito: 'Cartão de crédito',
  transferencia: 'Transferência',
  dinheiro: 'Dinheiro',
  debito_automatico: 'Débito automático',
  cartao_de_debito: 'Cartão de débito',
};

/* ------------------------------------------------------------- plano de contas */

/**
 * A natureza de uma categoria do plano: os mesmos quatro valores do tipo.
 *
 * É ela que decide em que lista do modal a categoria aparece e que tipo o
 * autopreenchimento infere de uma categoria padrão. Categoria fora do plano é
 * `despesa`, e é com essa natureza que nasce a categoria criada em Cadastros ou
 * acrescentada por uma gravação.
 */
export const NATUREZA_CATEGORIA = TIPO_LANCAMENTO;

/** A única categoria de crédito de movimentação, de sistema. */
export const CATEGORIA_CREDITO_MOVIMENTACAO = 'Créditos de Movimentação Bancária';

/** A única categoria de débito de movimentação, de sistema. */
export const CATEGORIA_DEBITO_MOVIMENTACAO = 'Débitos de Movimentação Bancária';

/**
 * A categoria padrão que abre a ajuda de custo no perfil do favorecido.
 *
 * Trocar o texto aqui sem trocar o da linha semeada pela `0022` faz o perfil de
 * todo colaborador perder a ajuda de custo ao ser salvo, porque a limpeza de
 * `perfilDoFavorecidoSchema` passa a não reconhecer a categoria.
 */
export const CATEGORIA_PESSOAL = 'Despesas com Pessoal';

/** A categoria padrão que abre a diarista no perfil do favorecido. */
export const CATEGORIA_PETRELLA = 'Petrella';

/**
 * A cor com que a categoria nova nasce.
 *
 * É **dado**, e não estilo: vai gravada na categoria. O desenho da categoria sem
 * cor usa o token `--emb-cinza-texto`, que tem o mesmo valor.
 */
export const COR_PADRAO_DA_CATEGORIA = '#6b7280';

/** Uma subcategoria como o plano de contas a devolve. */
export const subcategoriaDoPlanoSchema = z.object({
  id: idSchema,
  nome: z.string(),
  sistema: z.boolean(),
  emUso: z.boolean(),
});

/**
 * Uma categoria do plano de contas, com as subcategorias em ordem alfabética.
 *
 * `emUso` diz se algum lançamento a usa, e é o que recusa o 🗑. `cor` é nula na
 * categoria que só existe porque um lançamento a usou.
 */
export const categoriaSchema = z.object({
  id: idSchema,
  nome: z.string(),
  natureza: tipoLancamentoSchema,
  cor: z.string().nullable(),
  sistema: z.boolean(),
  emUso: z.boolean(),
  subcategorias: z.array(subcategoriaDoPlanoSchema),
});

export type Categoria = z.infer<typeof categoriaSchema>;

/** O plano inteiro, em ordem alfabética de categoria. */
export const planoDeContasSchema = z.array(categoriaSchema);

/** Uma cor `#rrggbb`, guardada em minúsculas. */
const corSchema = z
  .string()
  .trim()
  .regex(/^#[0-9a-fA-F]{6}$/, { message: 'Cor inválida (esperado #rrggbb).' })
  .transform((cor) => cor.toLowerCase());

/** O nome de uma subcategoria digitado numa ficha. */
const nomeDeSubcategoriaSchema = z
  .string()
  .trim()
  .min(1, { message: 'Informe o nome da subcategoria' })
  .max(120, { message: 'Deve ter no máximo 120 caracteres.' });

/** O que o modal "Nova Categoria" envia. A categoria nasce `despesa` e não é de sistema. */
export const novaCategoriaSchema = z.object({
  nome: z
    .string()
    .trim()
    .min(1, { message: 'Informe o nome da categoria' })
    .max(120, { message: 'Deve ter no máximo 120 caracteres.' }),
  cor: corSchema,
  subcategorias: z.array(nomeDeSubcategoriaSchema).default([]),
});

export type NovaCategoria = z.infer<typeof novaCategoriaSchema>;

/** O que o seletor de cor de uma categoria envia ao fechar. */
export const corDaCategoriaSchema = z.object({ cor: corSchema });

/** O que o modal "Nova Subcategoria" envia. */
export const novasSubcategoriasSchema = z.object({
  nomes: z
    .array(nomeDeSubcategoriaSchema)
    .min(1, { message: 'Adicione pelo menos uma subcategoria' }),
});

/**
 * Um padrão de favorecido: o perfil que um nome conhecido traz sem estar
 * cadastrado.
 *
 * O autopreenchimento do modal o aplica quando o nome digitado não é de
 * favorecido nenhum, e o favorecido criado com esse nome nasce com ele.
 */
export const padraoDeFavorecidoSchema = z.object({
  nome: z.string(),
  categoriaPadrao: z.string().nullable(),
  subcategoriaPadrao: z.string().nullable(),
  formaPagamentoPadrao: formaPagamentoLancamentoSchema.nullable(),
});

export type PadraoDeFavorecido = z.infer<typeof padraoDeFavorecidoSchema>;

/** A resposta de `GET /financas/padroes-de-favorecido`. */
export const padroesDeFavorecidoSchema = z.array(padraoDeFavorecidoSchema);

/** A resposta de acrescentar subcategorias: quantas não existiam e entraram. */
export const resultadoDasSubcategoriasSchema = z.object({ adicionadas: z.number().int() });

export type ResultadoDasSubcategorias = z.infer<typeof resultadoDasSubcategoriasSchema>;

/* ------------------------------------------------------------------- valores */

/**
 * O valor de um lançamento, com desconto e acréscimo aplicados.
 *
 * @param lancamento - Bruto, desconto e acréscimo, em reais.
 * @returns O valor da linha, sem arredondar: quem soma valores do caixa passa
 *   por `financas-calculo.ts`, que soma em centavos.
 */
export function valorDoLancamento(lancamento: {
  valorBruto: number;
  desconto: number;
  acrescimo: number;
}): number {
  return lancamento.valorBruto - lancamento.desconto + lancamento.acrescimo;
}

/* -------------------------------------------------------------- parcelamento */

/**
 * Quantas parcelas um parcelamento pode ter.
 *
 * O mínimo é dois porque um parcelamento de uma parcela é uma compra à vista, e
 * `parcelamentoCoerente` o recusa: gravado, ele faria a tela escrever "parcela 1
 * de 1" numa linha que não tem irmã nenhuma.
 */
export const MINIMO_DE_PARCELAS = 2;
export const MAXIMO_DE_PARCELAS = 60;

/** Quantos meses a recorrência mensal do modal pode cobrir. */
export const MAXIMO_DE_REPETICOES = 60;

/**
 * A posição de uma parcela, ou o total delas.
 *
 * Nulo é o lançamento à vista. O campo em branco vindo do formulário também
 * vira nulo, e não zero: zero descreveria uma parcela que não existe.
 */
export const parcelaSchema = z
  .union([z.coerce.number().int().positive().max(MAXIMO_DE_PARCELAS), z.literal(''), z.null()])
  .nullish()
  .transform((valor) => (typeof valor === 'number' ? valor : null));

/** De quanto em quanto tempo as parcelas vencem. */
export const INTERVALO_PARCELAMENTO = ['mensal', 'quinzenal', 'semanal', 'avulso'] as const;

export const intervaloParcelamentoSchema = z.enum(INTERVALO_PARCELAMENTO, {
  message: 'Intervalo de parcelamento desconhecido.',
});

export type IntervaloParcelamento = z.infer<typeof intervaloParcelamentoSchema>;

/** Quantos dias cada intervalo avança; `mensal` avança mês, e não trinta dias. */
const DIAS_DO_INTERVALO: Record<
  Exclude<IntervaloParcelamento, 'mensal' | 'avulso'>,
  number
> = {
  quinzenal: 15,
  semanal: 7,
};

/**
 * As datas em que as parcelas vencem, a partir da primeira.
 *
 * `mensal` anda de mês, e não de trinta dias: uma compra de 31 de janeiro em três
 * vezes vence em 28 de fevereiro e 31 de março. O dia que não existe no mês de
 * destino recua para o último dele. `avulso` devolve a primeira data repetida,
 * porque as datas de um parcelamento avulso são digitadas uma a uma.
 *
 * @param primeira - A data da primeira parcela, como `AAAA-MM-DD` e sem hora. Um
 *   texto de outra forma faz a função devolver essa mesma data repetida, em vez
 *   de inventar um calendário a partir de um valor que não soube ler.
 * @param parcelas - Quantas parcelas gerar.
 * @param intervalo - De quanto em quanto tempo elas vencem.
 * @returns Uma data por parcela, como `AAAA-MM-DD` e em ordem crescente.
 */
export function datasDasParcelas(
  primeira: string,
  parcelas: number,
  intervalo: IntervaloParcelamento,
): string[] {
  const quantas = Math.max(Math.trunc(parcelas), 1);
  const [ano, mes, dia] = primeira.split('-').map(Number);
  // A checagem de `NaN` vem junto com a de ausência: um texto com hora parte em
  // três pedaços e o terceiro vira `NaN`, que faria `toISOString` lançar.
  if (
    ano === undefined ||
    mes === undefined ||
    dia === undefined ||
    !Number.isInteger(ano) ||
    !Number.isInteger(mes) ||
    !Number.isInteger(dia)
  ) {
    return Array.from({ length: quantas }, () => primeira);
  }

  if (intervalo === 'avulso') return Array.from({ length: quantas }, () => primeira);

  return Array.from({ length: quantas }, (_, posicao) => {
    if (intervalo === 'mensal') {
      // `Date.UTC` com dia 0 do mês seguinte devolve o último dia do mês, que é
      // como o dia 31 recua para 28 ou 30 sem virar o mês.
      const alvo = new Date(Date.UTC(ano, mes - 1 + posicao, 1));
      const ultimoDia = new Date(
        Date.UTC(alvo.getUTCFullYear(), alvo.getUTCMonth() + 1, 0),
      ).getUTCDate();
      alvo.setUTCDate(Math.min(dia, ultimoDia));
      return alvo.toISOString().slice(0, 10);
    }

    const alvo = new Date(Date.UTC(ano, mes - 1, dia));
    alvo.setUTCDate(alvo.getUTCDate() + DIAS_DO_INTERVALO[intervalo] * posicao);
    return alvo.toISOString().slice(0, 10);
  });
}

/**
 * Diz se as três colunas de parcelamento estão coerentes entre si.
 *
 * Ou o lançamento é à vista e as três são nulas, ou ele é uma parcela e as três
 * estão preenchidas, com um total de pelo menos `MINIMO_DE_PARCELAS`.
 *
 * @param lancamento - As três colunas, como a linha as traz.
 * @returns Verdadeiro quando as três são nulas, ou quando as três estão
 *   preenchidas e `parcelaNumero` cabe no total.
 */
export function parcelamentoCoerente(lancamento: {
  parcelamentoId: string | null;
  parcelaNumero: number | null;
  parcelasTotal: number | null;
}): boolean {
  const { parcelamentoId, parcelaNumero, parcelasTotal } = lancamento;
  const preenchidas = [parcelamentoId, parcelaNumero, parcelasTotal].filter(
    (campo) => campo !== null,
  ).length;

  if (preenchidas === 0) return true;
  if (preenchidas !== 3) return false;
  if (parcelaNumero === null || parcelasTotal === null) return false;
  return parcelasTotal >= MINIMO_DE_PARCELAS && parcelaNumero <= parcelasTotal;
}

/* -------------------------------------------------------------------- modal */

/** A frase da origem para data, descrição ou valor ausentes (`saveEntry`). */
export const MENSAGEM_DOS_OBRIGATORIOS = 'Preencha data, descrição e valor.';

/** A frase da origem para o valor final que não passa de zero. */
export const MENSAGEM_DO_VALOR_FINAL = 'Valor final não pode ser zero ou negativo.';

/** A frase da origem para o parcelamento por datas com menos de duas. */
export const MENSAGEM_DAS_DATAS = 'Informe ao menos 2 datas.';

/** O valor bruto do modal: obrigatório, maior que zero, em centavos. */
const valorBrutoDoModalSchema = z
  .union([z.number(), z.string()])
  .transform((valor) => (typeof valor === 'number' ? valor : converterDinheiro(valor)))
  .refine((valor): valor is number => valor !== null && Number.isFinite(valor), {
    message: MENSAGEM_DOS_OBRIGATORIOS,
  })
  .transform(arredondarCentavos)
  .refine((valor) => valor > 0, { message: MENSAGEM_DOS_OBRIGATORIOS });

/**
 * O que o modal de lançamento envia, para criar e para editar.
 *
 * `favorecidoNome` é o texto do campo, e não uma chave: a API o casa com o
 * favorecido de mesmo nome na casa, sem diferenciar maiúsculas e sem os espaços
 * das pontas, e cria o favorecido que faltar. Vazio é lançamento sem favorecido.
 *
 * Não há provisão nem vínculo aqui: o corpo que os trouxer tem os campos
 * descartados pelo parse, e é a API quem decide os dois.
 */
export const lancamentoDoModalSchema = z
  .object({
    residenciaId: idSchema,
    favorecidoNome: textoOpcional(200),
    data: z.iso.date({ message: MENSAGEM_DOS_OBRIGATORIOS }),
    tipo: tipoLancamentoSchema,
    categoria: textoObrigatorio('Categoria', 120),
    subcategoria: textoOpcional(120),
    descricao: z
      .string()
      .trim()
      .min(1, { message: MENSAGEM_DOS_OBRIGATORIOS })
      .max(500, { message: 'Deve ter no máximo 500 caracteres.' }),
    formaPagamento: formaPagamentoLancamentoSchema.nullish().transform((v) => v ?? null),
    valorBruto: valorBrutoDoModalSchema,
    desconto: dinheiroComPadraoZeroSchema,
    acrescimo: dinheiroComPadraoZeroSchema,
    pago: z.boolean(),
    observacao: textoOpcional(1000),
  })
  .refine(
    (lancamento) => {
      // O Zod 4 roda este refinamento mesmo com erro num campo; o bruto que já
      // falhou (nulo ou zero) tem a sua frase, e repetir a do valor final daria
      // dois avisos para um engano só.
      if (!(lancamento.valorBruto > 0)) return true;
      const final = lancamento.valorBruto - lancamento.desconto + lancamento.acrescimo;
      return !Number.isFinite(final) || arredondarCentavos(final) > 0;
    },
    { message: MENSAGEM_DO_VALOR_FINAL, path: ['valorFinal'] },
  );

export type LancamentoDoModal = z.infer<typeof lancamentoDoModalSchema>;

/**
 * O que as duas seções de baixo do modal pedem: nada, a recorrência mensal ou o
 * parcelamento.
 *
 * A recorrência cria `meses` cópias pendentes e provisionadas nos meses
 * seguintes. O parcelamento por intervalo cria `parcelas` lançamentos de valor
 * cheio; o avulso, um por data, **na ordem em que as datas foram digitadas**.
 */
export const repeticaoSchema = z
  .union([
    z.object({
      tipo: z.literal('recorrencia'),
      meses: z.number().int().min(1).max(MAXIMO_DE_REPETICOES),
    }),
    z.object({
      tipo: z.literal('parcelamento'),
      intervalo: z.enum(['mensal', 'quinzenal', 'semanal']),
      parcelas: z.number().int().min(MINIMO_DE_PARCELAS).max(MAXIMO_DE_PARCELAS),
    }),
    z.object({
      tipo: z.literal('parcelamento'),
      intervalo: z.literal('avulso'),
      datas: z
        .array(dataSchema)
        .min(2, { message: MENSAGEM_DAS_DATAS })
        .max(MAXIMO_DE_PARCELAS),
    }),
  ])
  .nullable();

export type Repeticao = z.infer<typeof repeticaoSchema>;

/** O corpo de `POST /financas/lancamentos` e de `PUT /financas/lancamentos/{id}`. */
export const salvarLancamentoSchema = z.object({
  lancamento: lancamentoDoModalSchema,
  repeticao: repeticaoSchema.default(null),
});

export type SalvarLancamento = z.infer<typeof salvarLancamentoSchema>;

/* ---------------------------------------------------------------- respostas */

/** A residência do caixa, como a resposta a traz aninhada. */
export const residenciaDoCaixaSchema = z.object({
  id: idSchema,
  nome: z.string(),
  apelido: z.string(),
});

/** O favorecido do lançamento e do grupo, como a resposta o traz aninhado. */
export const favorecidoDoLancamentoSchema = z.object({
  id: idSchema,
  nome: z.string(),
});

/**
 * Um lançamento como a API o devolve.
 *
 * `status` vem calculado; `posicao` é a ordem dentro do mês, que vale como ordem
 * manual quando o mês tem uma e como desempate entre lançamentos do mesmo dia
 * quando não tem.
 */
export const lancamentoSchema = z.object({
  id: idSchema,
  residencia: residenciaDoCaixaSchema,
  favorecido: favorecidoDoLancamentoSchema.nullable(),
  data: z.string(),
  tipo: tipoLancamentoSchema,
  categoria: z.string(),
  subcategoria: z.string().nullable(),
  descricao: z.string(),
  formaPagamento: formaPagamentoLancamentoSchema.nullable(),
  valorBruto: z.number(),
  desconto: z.number(),
  acrescimo: z.number(),
  pago: z.boolean(),
  provisao: z.boolean(),
  status: statusLancamentoSchema,
  observacao: z.string().nullable(),
  posicao: z.number().int(),
  pedidoId: idSchema.nullable(),
  grupoAjudaCustoId: idSchema.nullable(),
  recorrenciaOrigemId: idSchema.nullable(),
  parcelamentoId: idSchema.nullable(),
  parcelaNumero: z.number().int().nullable(),
  parcelasTotal: z.number().int().nullable(),
  criadoEm: z.iso.datetime({ message: 'Instante inválido.' }),
  atualizadoEm: z.iso.datetime({ message: 'Instante inválido.' }),
});

export type Lancamento = z.infer<typeof lancamentoSchema>;

/** Um lançamento da listagem, com o saldo corrido; nulo no pendente. */
export const lancamentoDaListagemSchema = lancamentoSchema.extend({
  saldoCorrido: z.number().nullable(),
});

export type LancamentoDaListagem = z.infer<typeof lancamentoDaListagemSchema>;

/** A resposta de `GET /financas/lancamentos`: as linhas na ordem da tabela e o rodapé. */
export const listagemDeLancamentosSchema = z.object({
  lancamentos: z.array(lancamentoDaListagemSchema),
  soma: z.number(),
});

export type ListagemDeLancamentos = z.infer<typeof listagemDeLancamentosSchema>;

/** A resposta de criar e de editar pelo modal: as linhas gravadas, na ordem em que nasceram. */
export const lancamentosGravadosSchema = z.array(lancamentoSchema);

/* -------------------------------------------------------------------- filtro */

/**
 * Um parâmetro de busca opcional, em que o vazio da URL vale como ausente.
 *
 * @param schema - O schema do valor presente.
 * @returns O schema que devolve `undefined` para `''` e para a chave ausente.
 */
function opcionalNaBusca<T extends z.ZodType>(schema: T) {
  return z.preprocess((valor) => (valor === '' ? undefined : valor), schema.optional());
}

/**
 * Diz se a listagem lê o caixa inteiro, e não só um mês.
 *
 * A origem lê todos os meses quando a Pesquisa tem algo além de espaço ou quando
 * uma das pontas do período está preenchida, e só o mês aberto nos outros casos.
 *
 * @param filtro - A pesquisa e as duas pontas do período.
 * @returns Verdadeiro quando a fonte são todos os meses.
 */
export function listaTodosOsMeses(filtro: {
  pesquisa?: string;
  de?: string;
  ate?: string;
}): boolean {
  return (
    (filtro.pesquisa ?? '').trim() !== '' ||
    filtro.de !== undefined ||
    filtro.ate !== undefined
  );
}

/**
 * O recorte que `GET /financas/lancamentos` aceita, como parâmetros de busca.
 *
 * Sem pesquisa e sem período, `competencia` é obrigatória: é o mês aberto. O
 * período invertido é aceito e devolve a tabela vazia, como na origem.
 * `pesquisa` não é aparada: a origem procura o texto como foi digitado, e só usa
 * o texto aparado para decidir a fonte.
 */
export const filtroDeLancamentosSchema = z
  .object({
    residenciaId: idSchema,
    competencia: opcionalNaBusca(competenciaSchema),
    pesquisa: opcionalNaBusca(z.string().max(200)),
    de: opcionalNaBusca(dataSchema),
    ate: opcionalNaBusca(dataSchema),
    tipo: opcionalNaBusca(tipoLancamentoSchema),
    categoria: opcionalNaBusca(z.string().max(120)),
    status: opcionalNaBusca(z.enum(['pago', 'pendente'])),
    forma: opcionalNaBusca(formaPagamentoLancamentoSchema),
  })
  .refine((filtro) => listaTodosOsMeses(filtro) || filtro.competencia !== undefined, {
    message: 'Informe a competência.',
    path: ['competencia'],
  });

export type FiltroDeLancamentos = z.infer<typeof filtroDeLancamentosSchema>;

/* ---------------------------------------------------------- lote e ordem */

/** As cinco ações da barra de lote. */
export const ACAO_DO_LOTE = [
  'marcar_pago',
  'reverter_pendente',
  'marcar_lancado',
  'marcar_provisionado',
  'excluir',
] as const;

/**
 * O que a barra de lote envia. As cinco ações valem para os pagos também, como
 * na origem.
 */
export const loteDeLancamentosSchema = z.object({
  acao: z.enum(ACAO_DO_LOTE, { message: 'Ação desconhecida.' }),
  ids: z.array(idSchema).min(1, { message: 'Selecione ao menos um lançamento.' }).max(5000),
});

export type LoteDeLancamentos = z.infer<typeof loteDeLancamentosSchema>;
export type AcaoDoLote = LoteDeLancamentos['acao'];

/** A resposta do lote: quantos lançamentos existiam e foram alterados ou excluídos. */
export const resultadoDoLoteSchema = z.object({ alterados: z.number().int() });

export type ResultadoDoLote = z.infer<typeof resultadoDoLoteSchema>;

/** O que o arraste envia: quem foi solto, sobre quem, e se depois dele. */
export const reordenarLancamentoSchema = z.object({
  id: idSchema,
  alvoId: idSchema,
  depois: z.boolean(),
});

export type ReordenarLancamento = z.infer<typeof reordenarLancamentoSchema>;

/* ---------------------------------------------------------------- fechamento */

/** Um lançamento pago dentro de uma subcategoria do Fechamento. */
export const transacaoDoFechamentoSchema = z.object({
  id: idSchema,
  data: z.string(),
  descricao: z.string(),
  formaPagamento: formaPagamentoLancamentoSchema.nullable(),
  valor: z.number(),
  status: statusLancamentoSchema,
});

/** Uma subcategoria de uma tabela do Fechamento, com as transações por data. */
export const subcategoriaDoFechamentoSchema = z.object({
  nome: z.string(),
  valor: z.number(),
  lancamentos: z.array(transacaoDoFechamentoSchema),
});

/** Uma categoria de uma tabela do Fechamento, com as subcategorias por valor. */
export const categoriaDoFechamentoSchema = z.object({
  nome: z.string(),
  cor: z.string().nullable(),
  valor: z.number(),
  subcategorias: z.array(subcategoriaDoFechamentoSchema),
});

/** Uma das três tabelas por categoria do Fechamento. */
export const tabelaDoFechamentoSchema = z.object({
  total: z.number(),
  categorias: z.array(categoriaDoFechamentoSchema),
});

/**
 * O gráfico trimestral: um rótulo por trimestre até o do mês aberto, os meses
 * somados em cada um, o total de cada um e uma série por categoria.
 */
export const trimestresSchema = z.object({
  ano: z.number().int(),
  rotulos: z.array(z.string()),
  mesesPorTrimestre: z.array(z.array(z.string())),
  totais: z.array(z.number()),
  series: z.array(
    z.object({
      categoria: z.string(),
      cor: z.string().nullable(),
      valores: z.array(z.number()),
    }),
  ),
});

/**
 * A resposta de `GET /financas/fechamento`.
 *
 * `movimentacao` é nula no mês sem crédito nem débito de movimentação pago, e é
 * isso que esconde o cartão.
 */
export const fechamentoSchema = z.object({
  competencia: competenciaSchema,
  saldoInicial: z.number(),
  entradas: z.number(),
  saidas: z.number(),
  saldoFinal: z.number(),
  saidasPorCategoria: tabelaDoFechamentoSchema,
  entradasPorCategoria: tabelaDoFechamentoSchema,
  movimentacao: tabelaDoFechamentoSchema.nullable(),
  trimestres: trimestresSchema,
});

export type TransacaoDoFechamento = z.infer<typeof transacaoDoFechamentoSchema>;
export type SubcategoriaDoFechamento = z.infer<typeof subcategoriaDoFechamentoSchema>;
export type CategoriaDoFechamento = z.infer<typeof categoriaDoFechamentoSchema>;
export type TabelaDoFechamento = z.infer<typeof tabelaDoFechamentoSchema>;
export type Trimestres = z.infer<typeof trimestresSchema>;
export type Fechamento = z.infer<typeof fechamentoSchema>;

/** O recorte de `GET /financas/fechamento`: a casa do caixa e o mês aberto. */
export const consultaDoFechamentoSchema = z.object({
  residenciaId: idSchema,
  competencia: competenciaSchema,
});

export type ConsultaDoFechamento = z.infer<typeof consultaDoFechamentoSchema>;

/**
 * A resposta de `GET /financas/caixa`: em que casa o caixa está, o mês-base e o
 * Saldo Atual, que não depende de filtro nenhum.
 */
export const caixaSchema = z.object({
  residencia: residenciaDoCaixaSchema.nullable(),
  mesBase: competenciaSchema.nullable(),
  saldoAtual: z.number(),
});

export type Caixa = z.infer<typeof caixaSchema>;

/* ---------------------------------------------------------------- favorecido */

/**
 * O que o "+" do modal e o "+ Novo Favorecido" enviam: só o nome.
 *
 * O favorecido nasce com o padrão de favorecido de mesmo nome, quando há um; o
 * resto do perfil chega depois, por `perfilDoFavorecidoSchema`.
 */
export const novoFavorecidoSchema = z.object({
  residenciaId: idSchema,
  nome: textoObrigatorio('Nome', 200),
});

export type NovoFavorecido = z.infer<typeof novoFavorecidoSchema>;

/**
 * A ajuda de custo de um favorecido, como a API a devolve. Nulo é desabilitado.
 *
 * `custoDiaria` e `diariasDiaria` têm até quatro casas, porque a diária sai de um
 * salário dividido; `medicaValor` e `inssValor` são valores únicos, em centavos.
 * `recorrenciaDesde` é a competência a partir da qual a recorrência automática
 * gera os lançamentos.
 */
export const ajudaCustoDoFavorecidoSchema = z.object({
  custoDiaria: z.number().nullable(),
  diariasDiaria: z.number().nullable(),
  medicaValor: z.number().nullable(),
  inssValor: z.number().nullable(),
  alimentacaoValor: z.number().nullable(),
  recorrenciaDesde: competenciaSchema.nullable(),
});

export type AjudaCustoDoFavorecido = z.infer<typeof ajudaCustoDoFavorecidoSchema>;

/** A ajuda de custo de quem não é colaborador: tudo desabilitado. */
export const AJUDA_DE_CUSTO_DESABILITADA: AjudaCustoDoFavorecido = {
  custoDiaria: null,
  diariasDiaria: null,
  medicaValor: null,
  inssValor: null,
  alimentacaoValor: null,
  recorrenciaDesde: null,
};

/**
 * O CPF do perfil e do grupo de ajuda de custo: só os dígitos, até onze, sem
 * conferir o verificador, como a origem guarda — o campo da calculadora aceita
 * qualquer texto. Vazio vira `null`.
 */
export const cpfDoGrupoSchema = z
  .string()
  .trim()
  .nullish()
  .transform((valor) => {
    const digitos = apenasDigitos(valor ?? '');
    return digitos === '' ? null : digitos;
  })
  .refine((valor) => valor === null || valor.length <= 11, { message: 'CPF inválido.' });

/** O perfil como o modal o envia, antes das limpezas. */
const perfilDoFavorecidoBrutoSchema = z.object({
  categoriaPadrao: textoOpcional(120),
  subcategoriaPadrao: textoOpcional(120),
  formaPagamentoPadrao: formaPagamentoLancamentoSchema.nullish().transform((v) => v ?? null),
  cpf: cpfDoGrupoSchema,
  contaApartada: z.boolean(),
  contaApartadaDebitoDe: textoOpcional(200),
  contaApartadaBanco: textoOpcional(80),
  contaApartadaAgencia: textoOpcional(20),
  contaApartadaConta: textoOpcional(30),
  contaApartadaTipo: textoOpcional(30),
  ajudaCusto: z.object({
    custoDiaria: valorPorUnidadeOpcionalSchema,
    diariasDiaria: valorPorUnidadeOpcionalSchema,
    medicaValor: dinheiroOpcionalSchema,
    inssValor: dinheiroOpcionalSchema,
    alimentacaoValor: dinheiroOpcionalSchema,
    recorrenciaDesde: competenciaSchema.nullish().transform((v) => v ?? null),
  }),
  diaristaHabilitada: z.boolean(),
  diaristaValorDiaria: valorPorUnidadeOpcionalSchema,
});

/**
 * Aplica as limpezas de `saveFavModal` ao perfil.
 *
 * @param perfil - O perfil já validado campo a campo.
 * @returns O perfil com o que a origem apaga já apagado.
 */
function limparPerfil(perfil: z.output<typeof perfilDoFavorecidoBrutoSchema>) {
  const { contaApartada } = perfil;
  const pessoal = perfil.categoriaPadrao === CATEGORIA_PESSOAL;
  const diarista = perfil.categoriaPadrao === CATEGORIA_PETRELLA && perfil.diaristaHabilitada;

  return {
    ...perfil,
    contaApartadaDebitoDe: contaApartada ? perfil.contaApartadaDebitoDe : null,
    contaApartadaBanco: contaApartada ? perfil.contaApartadaBanco : null,
    contaApartadaAgencia: contaApartada ? perfil.contaApartadaAgencia : null,
    contaApartadaConta: contaApartada ? perfil.contaApartadaConta : null,
    contaApartadaTipo: contaApartada ? perfil.contaApartadaTipo : null,
    ajudaCusto: pessoal ? perfil.ajudaCusto : AJUDA_DE_CUSTO_DESABILITADA,
    diaristaHabilitada: diarista,
    diaristaValorDiaria: diarista ? (perfil.diaristaValorDiaria ?? 0) : null,
  };
}

/**
 * O que "Salvar Perfil" envia, já com as limpezas da origem aplicadas.
 *
 * Conta apartada desmarcada apaga os cinco campos dela; categoria padrão que não
 * é `CATEGORIA_PESSOAL` apaga a ajuda de custo e a recorrência; categoria que não
 * é `CATEGORIA_PETRELLA`, ou a caixa da diarista desmarcada, apaga a diarista; a
 * diarista marcada com o valor em branco grava zero. Na ajuda de custo, o nulo é
 * a linha desmarcada: a tela manda `0` para a linha marcada com o valor em
 * branco, e um nulo ali desabilitaria a linha.
 *
 * O nome não está aqui: o favorecido não se renomeia.
 */
export const perfilDoFavorecidoSchema = perfilDoFavorecidoBrutoSchema.transform(limparPerfil);

export type PerfilDoFavorecido = z.infer<typeof perfilDoFavorecidoSchema>;

/**
 * Um favorecido como a API o devolve.
 *
 * `emUso` diz se algum lançamento ou grupo de ajuda de custo aponta para ele, e é
 * o que desabilita o ✕ do cartão.
 */
export const favorecidoSchema = z.object({
  id: idSchema,
  residencia: residenciaDoCaixaSchema,
  nome: z.string(),
  categoriaPadrao: z.string().nullable(),
  subcategoriaPadrao: z.string().nullable(),
  formaPagamentoPadrao: formaPagamentoLancamentoSchema.nullable(),
  cpf: z.string().nullable(),
  contaApartada: z.boolean(),
  contaApartadaDebitoDe: z.string().nullable(),
  contaApartadaBanco: z.string().nullable(),
  contaApartadaAgencia: z.string().nullable(),
  contaApartadaConta: z.string().nullable(),
  contaApartadaTipo: z.string().nullable(),
  ajudaCusto: ajudaCustoDoFavorecidoSchema,
  diaristaHabilitada: z.boolean(),
  diaristaValorDiaria: z.number().nullable(),
  emUso: z.boolean(),
  criadoEm: z.iso.datetime({ message: 'Instante inválido.' }),
  atualizadoEm: z.iso.datetime({ message: 'Instante inválido.' }),
});

export type Favorecido = z.infer<typeof favorecidoSchema>;

/** A resposta de `GET /financas/favorecidos`, em ordem alfabética. */
export const favorecidosSchema = z.array(favorecidoSchema);

/** O recorte de `GET /financas/favorecidos`: a casa do caixa. */
export const consultaDosFavorecidosSchema = z.object({ residenciaId: idSchema });

/* ------------------------------------------------------------ ajuda de custo */

/** Um item de um grupo de ajuda de custo, como o formulário o envia. */
export const novoItemAjudaCustoSchema = z.object({
  tipo: tipoItemAjudaCustoSchema,
  descricao: textoObrigatorio('Descrição', 300),
  valor: dinheiroSchema,
  valorDiaria: valorPorUnidadeOpcionalSchema,
  dias: z
    .union([z.number(), z.string()])
    .nullish()
    .transform((valor) => {
      if (valor == null || valor === '') return null;
      return typeof valor === 'number' ? valor : Number(valor.trim());
    })
    .refine((valor) => valor === null || (Number.isInteger(valor) && valor >= 0), {
      message: 'Os dias precisam ser um número inteiro e não negativo.',
    }),
});

export type NovoItemAjudaCusto = z.infer<typeof novoItemAjudaCustoSchema>;

/**
 * Um grupo de ajuda de custo: os lançamentos que uma folha gerou, com quem pagou,
 * de que competência é e se já foi pago. O total é a soma dos itens.
 *
 * O `cpf` é o que o recibo do grupo imprime, e não o do cadastro: a calculadora
 * o grava como estava no campo, só com os dígitos.
 */
export const novoGrupoAjudaCustoSchema = z.object({
  favorecidoId: idSchema,
  pagador: textoObrigatorio('Pagador', 200),
  cpf: cpfDoGrupoSchema,
  competencia: competenciaSchema,
  ferias: z.boolean().default(false),
  pago: z.boolean().default(false),
  dataPagamento: dataOpcionalSchema,
  itens: z.array(novoItemAjudaCustoSchema).default([]),
});

export type NovoGrupoAjudaCusto = z.infer<typeof novoGrupoAjudaCustoSchema>;

/** Um item de ajuda de custo como a API o devolve. */
export const itemAjudaCustoSchema = z.object({
  id: idSchema,
  posicao: z.number().int(),
  tipo: tipoItemAjudaCustoSchema,
  descricao: z.string(),
  valor: z.number(),
  valorDiaria: z.number().nullable(),
  dias: z.number().int().nullable(),
});

export type ItemAjudaCusto = z.infer<typeof itemAjudaCustoSchema>;

/** Um grupo de ajuda de custo como a API o devolve. */
export const grupoAjudaCustoSchema = z.object({
  id: idSchema,
  favorecido: favorecidoDoLancamentoSchema,
  pagador: z.string(),
  cpf: z.string().nullable(),
  competencia: competenciaSchema,
  ferias: z.boolean(),
  pago: z.boolean(),
  dataPagamento: z.string().nullable(),
  itens: z.array(itemAjudaCustoSchema),
  criadoEm: z.iso.datetime({ message: 'Instante inválido.' }),
  atualizadoEm: z.iso.datetime({ message: 'Instante inválido.' }),
});

export type GrupoAjudaCusto = z.infer<typeof grupoAjudaCustoSchema>;

/**
 * O total de um grupo de ajuda de custo.
 *
 * @param itens - Os itens do grupo.
 * @returns A soma dos valores; zero num grupo sem itens.
 */
export function totalDoGrupo(itens: readonly { valor: number }[]): number {
  return itens.reduce((soma, item) => soma + item.valor, 0);
}
